import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useTenantQueryActions } from '@/api/hooks/tenantQueryActions';
import type { TenantAiContextPayload } from '@/api/schema/data-contracts';
import { Button } from '@/components/atoms/Button';
import { Unauthorized } from '@/components/atoms/Unauthorized';
import { AdminBlockComponent } from '@/components/molecules/AdminBlockComponent';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';
import { useHasGlobalPermission } from '@/hooks/useHasGlobalPermission';
import { AiAdvisoryAdminLayout } from '@/pages/administration/tabs/ai-advisory-layout/AiAdvisoryAdminLayout';
import { AdministrationTabsWrapper } from '@/pages/administration/tabs/wrapper';

import {
  countConfiguredSections,
  defaultTenantContextForm,
  mergeTenantContext,
  normalizeTenantContextForSave,
  TOTAL_SECTIONS,
} from './helpers';
import { TenantContextMessages } from './messages';

const inputClass =
  'mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-c1 focus:outline-hidden focus:ring-2 focus:ring-c1/50 dark:border-gray-600 dark:border-white dark:bg-c16 dark:text-gray-100 dark:placeholder-gray-500';

const TenantContextTab = () => {
  const messages = useMessages(TenantContextMessages);
  const intl = useIntl();
  const toast = useToast();
  const hasPermission = useHasGlobalPermission('CAN_MANAGE_TENANT_SETTINGS');

  const { useGetTenantAiConfig, useUpdateTenantAiConfig } = useTenantQueryActions();
  // Gate the fetch, not just the render (EWM-950).
  const { data: aiConfig, isLoading } = useGetTenantAiConfig({
    enabled: hasPermission === true,
  });
  const { mutateAsync: updateAiConfig, isPending } = useUpdateTenantAiConfig();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty },
  } = useForm<TenantAiContextPayload>({
    defaultValues: defaultTenantContextForm(),
  });

  const formValues = watch();
  const configuredCount = countConfiguredSections(formValues);

  useEffect(() => {
    if (aiConfig && !isLoading && !isDirty) {
      reset(mergeTenantContext(aiConfig.tenantContext ?? undefined));
    }
  }, [aiConfig, isLoading, isDirty, reset]);

  const onSubmit = async (data: TenantAiContextPayload) => {
    try {
      const normalized = normalizeTenantContextForSave(data);
      const response = await updateAiConfig({ tenantContext: normalized ?? undefined });
      reset(mergeTenantContext(response.tenantContext ?? undefined));
      toast.showToast(messages.saveSuccess(), 'success');
    } catch {
      toast.showToast(messages.saveError(), 'error');
    }
  };

  if (!hasPermission) {
    return <Unauthorized />;
  }

  return (
    <AdministrationTabsWrapper>
      <AiAdvisoryAdminLayout activeTab='tenant-context'>
        <div className='align-center mb-5 flex flex-wrap items-start gap-4'>
          <div className='min-w-0 flex-1'>
            <h1 className='m-0'>{messages.header()}</h1>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
              {intl.formatMessage(TenantContextMessages.completeness, {
                configured: configuredCount,
                total: TOTAL_SECTIONS,
              })}
            </p>
          </div>
          <div className='flex shrink-0 items-center gap-2'>
            <Button
              variant='success'
              size='sm'
              outlined={true}
              type='submit'
              form='tenant-context-form'
              isProcessing={isPending}
            >
              {messages.saveTenantContext()}
            </Button>
          </div>
        </div>

        <form id='tenant-context-form' className='space-y-4' onSubmit={handleSubmit(onSubmit)}>
          <AdminBlockComponent name={messages.section1()} showDelete={false} initCollapsed={false}>
            <div className='mt-3 mb-3'>
              <label className='block text-sm'>
                {messages.s1FieldName()}
                <input
                  type='text'
                  className={inputClass}
                  placeholder='e.g. ACME Bank'
                  maxLength={500}
                  {...register('tenantIdentity.name')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s1FieldIndustry()}
                <input
                  type='text'
                  className={inputClass}
                  placeholder='e.g. Banking, Insurance, Retail'
                  maxLength={500}
                  {...register('tenantIdentity.industry')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s1FieldSubIndustry()}
                <input
                  type='text'
                  className={inputClass}
                  placeholder='e.g. Corporate lending'
                  maxLength={500}
                  {...register('tenantIdentity.subIndustry')}
                />
              </label>
            </div>
            <div>
              <label className='block text-sm'>
                {messages.s1FieldCompanySize()}
                <input
                  type='text'
                  className={inputClass}
                  placeholder='e.g. 5,000–15,000 employees'
                  maxLength={500}
                  {...register('tenantIdentity.companySize')}
                />
              </label>
            </div>
          </AdminBlockComponent>

          <AdminBlockComponent name={messages.section2()} showDelete={false}>
            <div className='mt-3 mb-3'>
              <label className='block text-sm'>
                {messages.s2FieldCountries()}
                <textarea
                  rows={3}
                  className={inputClass}
                  placeholder='e.g. DE, AT, CH; primary: DACH'
                  maxLength={2000}
                  {...register('geography.countries')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s2FieldPrimaryRegions()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('geography.primaryRegions')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s2FieldLanguages()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('geography.languages')}
                />
              </label>
            </div>
            <div>
              <label className='block text-sm'>
                {messages.s2FieldTimeZones()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('geography.timeZones')}
                />
              </label>
            </div>
          </AdminBlockComponent>

          <AdminBlockComponent name={messages.section3()} showDelete={false}>
            <div className='mt-3 mb-3'>
              <label className='block text-sm'>
                {messages.s3FieldRegulations()}
                <textarea
                  rows={3}
                  className={inputClass}
                  maxLength={4000}
                  {...register('regulatory.applicableRegulations')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s3FieldDataSensitivity()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('regulatory.dataSensitivity')}
                />
              </label>
            </div>
            <div>
              <label className='block text-sm'>
                {messages.s3FieldCompliancePosture()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('regulatory.compliancePosture')}
                />
              </label>
            </div>
          </AdminBlockComponent>

          <AdminBlockComponent name={messages.section4()} showDelete={false}>
            <div className='mt-3 mb-3'>
              <label className='block text-sm'>
                {messages.s4FieldHostingModel()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('technology.hostingModel')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s4FieldCloudProviders()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('technology.cloudProviders')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s4FieldTechStack()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('technology.techStack')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s4FieldIntegrationPatterns()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('technology.integrationPatterns')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s4FieldDatabases()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('technology.databases')}
                />
              </label>
            </div>
            <div>
              <label className='block text-sm'>
                {messages.s4FieldDevOpsTooling()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('technology.devOpsTooling')}
                />
              </label>
            </div>
          </AdminBlockComponent>

          <AdminBlockComponent name={messages.section5()} showDelete={false}>
            <div className='mt-3 mb-3'>
              <label className='block text-sm'>
                {messages.s5FieldDeliveryModel()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('organizational.deliveryModel')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s5FieldMethodologies()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('organizational.methodologies')}
                />
              </label>
            </div>
            <div>
              <label className='block text-sm'>
                {messages.s5FieldWaysOfWorking()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('organizational.waysOfWorking')}
                />
              </label>
            </div>
          </AdminBlockComponent>

          <AdminBlockComponent name={messages.section6()} showDelete={false}>
            <div className='mt-3 mb-3'>
              <label className='block text-sm'>
                {messages.s6FieldSecurityLevel()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('securityPosture.securityLevel')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s6FieldIdentityStandards()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('securityPosture.identityStandards')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s6FieldDataClassification()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('securityPosture.dataClassification')}
                />
              </label>
            </div>
            <div>
              <label className='block text-sm'>
                {messages.s6FieldSecurityReview()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('securityPosture.securityReviewExpectation')}
                />
              </label>
            </div>
          </AdminBlockComponent>

          <AdminBlockComponent name={messages.section7()} showDelete={false}>
            <div className='mt-3 mb-3'>
              <label className='block text-sm'>
                {messages.s7FieldAvailability()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('nonFunctionalDefaults.availabilityTarget')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s7FieldPerformance()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('nonFunctionalDefaults.performanceExpectations')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s7FieldSupportHours()}
                <input
                  type='text'
                  className={inputClass}
                  maxLength={500}
                  {...register('nonFunctionalDefaults.supportHours')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s7FieldEnvironments()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('nonFunctionalDefaults.environments')}
                />
              </label>
            </div>
            <div>
              <label className='block text-sm'>
                {messages.s7FieldDocumentation()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('nonFunctionalDefaults.documentationExpectations')}
                />
              </label>
            </div>
          </AdminBlockComponent>

          <AdminBlockComponent name={messages.section8()} showDelete={false}>
            <div className='mt-3'>
              <label className='block text-sm'>
                {messages.s8FieldDayRates()}
                <textarea
                  rows={6}
                  className={inputClass}
                  placeholder='Internal IT/Business, external junior/regular/senior, domain experts…'
                  maxLength={4000}
                  {...register('resourceCosts.dayRatesByCategory')}
                />
              </label>
            </div>
          </AdminBlockComponent>

          <AdminBlockComponent name={messages.section9()} showDelete={false}>
            <div className='mt-3 mb-3'>
              <label className='block text-sm'>
                {messages.s9FieldRiskAppetite()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('riskCulture.riskAppetite')}
                />
              </label>
            </div>
            <div className='mb-3'>
              <label className='block text-sm'>
                {messages.s9FieldChangeManagement()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('riskCulture.changeManagementRigor')}
                />
              </label>
            </div>
            <div>
              <label className='block text-sm'>
                {messages.s9FieldOversightStyle()}
                <textarea
                  rows={2}
                  className={inputClass}
                  maxLength={2000}
                  {...register('riskCulture.projectOversightStyle')}
                />
              </label>
            </div>
          </AdminBlockComponent>

          <AdminBlockComponent name={messages.section10()} showDelete={false}>
            <div className='mt-3'>
              <label className='block text-sm'>
                {messages.s10FieldDeliverables()}
                <textarea
                  rows={5}
                  className={inputClass}
                  placeholder='e.g. Project Lessons Learned; Updated Operations Documentation'
                  maxLength={4000}
                  {...register('mandatoryDeliverables.deliverablesList')}
                />
              </label>
            </div>
          </AdminBlockComponent>
        </form>
      </AiAdvisoryAdminLayout>
    </AdministrationTabsWrapper>
  );
};

export default TenantContextTab;
